import i18n, { STORAGE_KEY, SUPPORTED_LOCALES, setLocale, type SupportedLocale } from "./i18n";

declare global {
    interface Window {
        dataLayer: unknown[];
    }
}

const APP_ENV = import.meta.env.VITE_APP_ENV as string | undefined;
const MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined;

// Локальная разработка никогда не репортит в GA, даже если measurement ID
// попал в сборку — только реальные деплои.
const ANALYTICS_ENVS = new Set(["test", "production"]);

// Устанавливается один раз при вставке тега gtag.js — initAnalytics может
// вызываться повторно при повторном согласии в рамках одной сессии, скрипт
// не должен вставляться дважды.
let loaded = false;

// Вызывается только после явного согласия на аналитические cookie (см.
// store/cookieConsent.ts), никогда безусловно при загрузке приложения.
export function initAnalytics(): void {
    if (!MEASUREMENT_ID || !APP_ENV || !ANALYTICS_ENVS.has(APP_ENV)) return;
    if (loaded) {
        setGaDisabled(false);
        return;
    }
    loaded = true;

    window.dataLayer = window.dataLayer || [];
    function gtag(...args: unknown[]) {
        window.dataLayer.push(args);
    }
    gtag("js", new Date());
    gtag("config", MEASUREMENT_ID);

    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
    document.head.appendChild(script);
}

// `ga-disable-<id>` — официальный флаг отключения gtag.js, проверяется на каждом
// вызове и работает даже после загрузки скрипта в этой сессии; заодно best-effort
// чистит уже выставленные _ga* cookie.
export function disableAnalytics(): void {
    if (!MEASUREMENT_ID) return;
    setGaDisabled(true);
    document.cookie.split(";").forEach((entry) => {
        const name = entry.split("=")[0].trim();
        if (name.startsWith("_ga")) {
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
        }
    });
}

function setGaDisabled(disabled: boolean): void {
    (window as unknown as Record<string, boolean>)[`ga-disable-${MEASUREMENT_ID}`] = disabled;
}

// Полностью исключает текущего пользователя из статистики (и Plausible, и
// GA), независимо от согласия на аналитику и от того, в каком порядке всё
// успело инициализироваться - оба инструмента используют штатные флаги,
// которые проверяются перед КАЖДЫМ хитом, а не только при первой загрузке
// скрипта: Plausible - localStorage-флаг plausible_ignore (официальный
// механизм самоисключения, plausible.io/docs/excluding-visits), GA - тот
// же `ga-disable-<id>`, что и disableAnalytics() выше. Поэтому вызов
// безопасен даже до того, как согласие на cookie/сами скрипты вообще
// загрузились - см. вызов из App.vue (watch на auth.isAdmin, немедленно и
// реактивно: admin-статус текущего пользователя подтягивается асинхронно,
// GET /auth/me, и может стать известен уже ПОСЛЕ применения согласия).
const PLAUSIBLE_IGNORE_KEY = "plausible_ignore";

export function setAdminTrackingExcluded(isAdmin: boolean): void {
    if (isAdmin) {
        localStorage.setItem(PLAUSIBLE_IGNORE_KEY, "true");
    } else {
        localStorage.removeItem(PLAUSIBLE_IGNORE_KEY);
    }
    setGaDisabled(isAdmin);
}

declare global {
    interface Window {
        plausible?: {
            (...args: unknown[]): void;
            q?: unknown[];
            init?: (opts?: Record<string, unknown>) => void;
            o?: Record<string, unknown>;
        };
    }
}

// Plausible (self-hosted, stats.ecoway.kz - см. CLAUDE.local.md) - теперь
// собирает данные и с test-стенда тоже (свой отдельный сайт в дашборде,
// свой VITE_PLAUSIBLE_SCRIPT_SRC в docker-compose.test.yml - для
// тестирования настройки перед прод-изменениями), тем же способом, что и
// GA выше. Гейтится тем же согласием на аналитические cookie, что и GA,
// хотя сам Plausible cookie не использует - единый переключатель проще,
// чем два независимых.
//
// Полный URL скрипта (не просто домен) - в этой версии Plausible (CE, per-site
// script) идентификатор сайта зашит в само имя файла скрипта
// (.../js/pa-<id>.js, выдаётся один раз при добавлении сайта в дашборде),
// никакого атрибута data-domain на теге больше нет - см. снипет со страницы
// установки сайта в дашборде, если понадобится перевыпустить.
const PLAUSIBLE_SCRIPT_SRC = import.meta.env.VITE_PLAUSIBLE_SCRIPT_SRC as string | undefined;
const PLAUSIBLE_ENVS = new Set(["test", "production"]);
let plausibleLoaded = false;

// Языковые custom properties, прикладываются к каждому событию Plausible:
// - locale: язык интерфейса, реально показанный пользователю;
// - system_locale: основной язык браузера/системы (kz -> kk), так видно,
//   у скольких системный язык не совпадает с интерфейсом;
// - locale_chosen: "yes", если пользователь сам переключал язык (в
//   localStorage лежит выбранная локаль), "no" - язык определён автоматически.
// Для доли сменивших язык см. также событие "Locale: Change" (changeLocale()).
function systemLocale(): string {
    const lang = (navigator.languages?.[0] || navigator.language || "").toLowerCase().split("-")[0];
    return lang === "kz" ? "kk" : lang || "unknown";
}

function localeProps(): Record<string, string> {
    let chosen = false;
    try {
        chosen = (SUPPORTED_LOCALES as readonly string[]).includes(localStorage.getItem(STORAGE_KEY) ?? "");
    } catch {
        // localStorage может быть недоступен (приватный режим) - считаем "не выбирал".
    }
    return { locale: i18n.global.locale.value, system_locale: systemLocale(), locale_chosen: chosen ? "yes" : "no" };
}

export function initPlausible(): void {
    if (!PLAUSIBLE_SCRIPT_SRC || !APP_ENV || !PLAUSIBLE_ENVS.has(APP_ENV) || plausibleLoaded) return;
    plausibleLoaded = true;

    window.plausible =
        window.plausible ||
        function (...args: unknown[]) {
            (window.plausible!.q = window.plausible!.q || []).push(args);
        };
    window.plausible.init = window.plausible.init || ((opts) => (window.plausible!.o = opts || {}));
    // customProperties в init() - штатный способ Plausible задать custom
    // property, которое автоматически прикладывается к КАЖДОМУ событию (не
    // только автотрекнутому pageview при загрузке, но и последующим SPA-
    // переходам, т.к. i.customProperties читается на каждый вызов m() - см.
    // window.plausible.o + S()/m() в самом script.js, .../js/pa-<id>.js).
    // Ключ именно "customProperties", НЕ "props" - "props" молча
    // игнорируется библиотекой (m() читает `m&&m.props`, только для опций
    // ВЫЗОВА plausible('event', {props}), а не для init()) - воспроизведено
    // и проверено на реальном pa-<id>.js с обоих сайтов (prod и test).
    // Локаль снимается один раз, на момент инициализации (согласие на
    // cookie - единственное, что триггерит initPlausible(), и на этот
    // момент i18n.global.locale.value уже определена detectLocale()'ом).
    window.plausible.init({ customProperties: localeProps() });

    const script = document.createElement("script");
    script.async = true;
    script.src = PLAUSIBLE_SCRIPT_SRC;
    document.head.appendChild(script);
}

// Кастомные события (линейка, поиск/просмотр/скачивание маршрутов, привет-
// лента, таймлапсы, камеры - см. соответствующие вызовы по кодовой базе).
// Гейтится тем же plausibleLoaded, что и сам initPlausible() - до согласия
// на аналитические cookie/до нужного окружения событие просто не шлётся,
// вместо накопления в очереди на неопределённый срок.
export function trackEvent(name: string, props?: Record<string, string | number | boolean>): void {
    if (!plausibleLoaded) return;
    window.plausible?.(name, props ? { props } : undefined);
}

// Явная смена языка пользователем (LanguageSwitcher, настройки профиля) -
// единственное место, откуда её надо вызывать вместо голого setLocale():
// шлёт событие "Locale: Change" {from, to, system}, по которому в дашборде
// считается доля пользователей, менявших язык интерфейса. Автоопределение
// локали (detectLocale) сюда не попадает - это не выбор пользователя.
export function changeLocale(to: SupportedLocale): void {
    const from = i18n.global.locale.value;
    if (from === to) return;
    setLocale(to);
    // Best-effort: если скрипт Plausible читает window.plausible.o на каждый
    // вызов (см. комментарий в initPlausible), последующие события сразу
    // получат новый язык; иначе он обновится после перезагрузки страницы.
    if (window.plausible?.o) window.plausible.o.customProperties = localeProps();
    trackEvent("Locale: Change", { from, to, system: systemLocale() });
}
